import React, { createContext, useState, useEffect, useContext } from "react";
import _ from "lodash";
import moment from "moment";
import { OrgContext } from "./OrgContext";
import { UserContext } from "./UserContext";
import I18n from "../common/I18n";
import { getFromToDate } from "../components/orders/helpers";
import { message } from "antd";
import { placeholder } from "@babel/types";
import { getValueFromArrayOfObjects } from "../helpers/array_functions";
import { catalog as fetchMoveTypeCatalog } from "../api/ServiceLevels";
import { isLtlOrganization } from "../helpers/orders";

const OrderConfigContext = createContext({});

const getDefaultGlobalFilter = (fromDate, toDate) => {
  return {
    fromDate,
    toDate,
    filter: "VERIFIED",
    sortBy: "none",
    sortByType: "descend",
    search_key_type: "",
    search_type : "EQUALS",
    schedule_orders_type: "SCHEDULED",
    search_order_token : "",
    account_codes: [],
    zone_ids: [],
    order_type: "",
    vehicle_type: "",
    schedule_day_filter: "Current Week",
    search_key_value : "",
    created_by_ids: [],
    insertion_type: [],
    service_level_ids: [],
    order_label: "",
    origin_wh_location_id: "",
    destination_wh_location_id: "",
    exclude_completed_orders: false,
    is_imported: false,
    is_inbound: false
    }
}

const OrderConfigContextProvider = ({ children }) => {
  const { fromDate, toDate } = getFromToDate('Current Week')
  const [ filterPlaceHolder, setFilterPlaceHolder ] = useState(getDefaultGlobalFilter(fromDate, toDate));
  const [paginationData, setPaginationData] = useState({});
  const [recordsPerPage, setRecordPerPage] = useState("");
  const [orderSelectedInfo, setOrderSelectedInfo] = useState([]);
  const [selectedException, setSelectedException] = useState("");
  const orgContextData = useContext(OrgContext);
  const { isAirCargo, currentOrg, orgSettings } = orgContextData;
  const isOrgAirCargo = isAirCargo();
  // P6c: org move-type catalog indexed by order_label -> { short_code,
  // display_color, alias_name }, fetched once here (single shared provider) and
  // consumed by TypeOfOrder to render the org-configured badge instead of the
  // hardcoded SVG icon. Populated only when service_level_flow_enabled is on;
  // empty otherwise, so consumers fall back to the legacy icon (dark by default).
  const [omtByOrderLabel, setOmtByOrderLabel] = useState({});
  const { orgRules } = useContext(UserContext);
  const isCLIEnabled = orgRules?.org_billing_rules?.cli_order_types === 'true';


  const statusForActions = {
    "SCHEDULE":  { status: ["NEW", "RECEIVED", "VERIFIED", "ON_HOLD", "ON_HAND"], isIncludes : true },
    "MOVE_TO_RECEIVED":  { status: ["NEW"], isIncludes : true },
    "QUICK_DISPATCH":  { status: ["NEW", "RECEIVED", "VERIFIED", "ON_HAND"],isIncludes : true },
    "MOVE_TO_VERIFY":  { status: ["RECEIVED"],isIncludes : true },
    "CHANGE_STATUS":   { status: ["PENDING", "REJECTED"],isIncludes : false },
    "PRINT_LABELS":  { status: ["PENDING", "REJECTED"],isIncludes : false },
    "CLONE_ORDERS":  { status: ["NEW", "RECEIVED", "VERIFIED", "ON_HOLD"],isIncludes : true },
    "LINEHAUL":  { status: ["NEW", "RECEIVED", "VERIFIED"],isIncludes : true },
    "SWITCH_ORDERS":  { status: ["NEW", "RECEIVED", "VERIFIED"],isIncludes : true },
    // ON_HAND is only a valid pre-status for LTL orgs - an LTL order can be run through Freight
    // Status again (e.g. an off-path hop, or its own next OHO/OHD leg) after already being marked
    // On Hand once. Non-LTL orgs keep the original list.
    "FSU_UPDATE":  { status: isLtlOrganization(currentOrg)
      ? ["NEW", "RECEIVED", "VERIFIED", "ON_HOLD", "ON_HAND"]
      : ["NEW", "RECEIVED", "VERIFIED", "ON_HOLD"],isIncludes : true },
    // ponytail: provisional eligibility — tighten once tender backend defines the rule
    "TENDER":  { status: ["NEW", "RECEIVED", "VERIFIED", "ON_HOLD", "ASSIGNED", "DISPATCHED"],isIncludes : true },
    "BULK_DELETE":  { status: [
      "NEW",
      "RECEIVED",
      "VERIFIED",
      "PENDING",
      "APPROVED",
      "REJECTED",
      "ON_HOLD",
    ],isIncludes : true },
    "DISPATCH_STATUS": {
      status: [
        "NEW",
        "RECEIVED",
        "VERIFIED",
        "PENDING",
        "APPROVED",
        "REJECTED",
        "ON_HOLD",
      ], isIncludes: false
    },
  }
  const orderConfig = {
    orderTypeKeysForFilter :  {
      "D,TD": "Delivery",
      "R,TR": isOrgAirCargo ? "Pickup" : "Return",
      "T": "Transfer",
      "LH": "Linehaul",
    },
    ORDER_TYPES:  {
      D: {
        label: "Delivery",
        symbol: "D",
      }
      , R: {
        label: isOrgAirCargo ? "Pickup" : "Return",
        symbol: "R",
      },
      M : {
        label: "Move",
        symbol: "M",
      },
      // LH : {
      //   label: "Linehaul",
      //   symbol: "LH",
      // },
      T : {
        label: "Transfer",
        symbol: "T",
      },
    },
    orderTypes: {
      D: {
        label: "Delivery",
        orderTypes: ["D"],
        is_location_based: false,
        types: [
          {
            type_of_order: "D",
            key: "drop_params",
            label: "Delivery",
            is_location_based: false,
          },
        ],
      },
      R: {
        label: isOrgAirCargo ? "Pickup" : "Return",
        orderTypes: ["R"],
        is_location_based: false,
        types: [
          {
            type_of_order: "R",
            key: "pickup_params",
            label: "Pickup",
            is_location_based: false,
          },
        ],
      },
      X: {
        label: "Exchange",
        orderTypes: ["R", "D"],
        is_location_based: false,
        types: [
          {
            type_of_order: "X",
            key: "exchange_params",
            label: "Order",
            is_location_based: false,
          },
        ],
      },
      MS: {
        label: "Multistop",
        orderTypes: ["MS"],
        is_location_based: true,
        types: [
          {
            type_of_order: "MS",
            key: "transfer_params",
            label: "Multistop",
            is_location_based: true,
          },
        ]
      },
       M: {
        label: "Move",
        orderTypes: ["TR", "TD"],
        is_location_based: false,
        types: [
          {
            type_of_order: "TR",
            key: "pickup_params",
            label: "Pickup",
            is_location_based: false,
          },
          {
            type_of_order: "TD",
            key: "drop_params",
            label: "Delivery",
             is_location_based: false,
          },
        ],
      },
      // LH: {
      //   label: "Linehaul",
      //   orderTypes: ["LH"],
      //   is_location_based: true,
      //   types: [
      //     {
      //       type_of_order: "LH",
      //       key: "linehaul_params",
      //       label: "Linehaul",
      //       is_location_based: true,
      //     },
      //   ],
      // },
      T: {
        label: "Transfer",
        orderTypes: ["T"],
        is_location_based: true,
        types: [
          {
            type_of_order: "T",
            key: "transfer_params",
            label: "Transfer",
            is_location_based: true,
          },
        ],
      },
      LH: {
        label: "Linehaul",
        orderTypes: ["LH"],
        is_location_based: true,
        types: [
          {
            type_of_order: "LH",
            key: "transfer_params",
            label: "Linehaul",
            is_location_based: true,
          },
        ],
      },
      // CDKD: {
      //   label: "Cross Dock & Delivery",
      //   orderTypes: ["T"],
      //   is_location_based: true,
      //   types: [
      //     {
      //       type_of_order: "T",
      //       key: "transfer_params",
      //       label: "Cross Dock & Delivery",
      //       is_location_based: true,
      //     },
      //   ],
      // },

      // PCDK: {
      //   label: "Pickup & Cross Dock",
      //   orderTypes: ["T"],
      //   is_location_based: true,
      //   types: [
      //     {
      //       type_of_order: "T",
      //       key: "pickup_params",
      //       label: "Pickup & Cross Dock",
      //       is_location_based: true,
      //     },
      //   ],
      // },
  
    },
    quoteOrderTypes: {
      D: {
        label: "Delivery",
        orderTypes: ["D"],
        is_location_based: false,
        types: [
          {
            type_of_order: "D",
            key: "drop_params",
            label: "Delivery",
            is_location_based: false,
          },
        ],
      },
      R: {
        label: isOrgAirCargo ? "Pickup" : "Return",
        orderTypes: ["R"],
        is_location_based: false,
        types: [
          {
            type_of_order: "R",
            key: "pickup_params",
            label: "Pickup",
            is_location_based: false,
          },
        ],
      },
      X: {
        label: "Exchange",
        orderTypes: ["R", "D"],
        is_location_based: false,
        types: [
          {
            type_of_order: "X",
            key: "exchange_params",
            label: "Order",
            is_location_based: false,
          },
        ],
      },
      M: {
        label: "Move",
        orderTypes: ["TR", "TD"],
        is_location_based: false,
        types: [
          {
            type_of_order: "TR",
            key: "pickup_params",
            label: "Pickup",
            is_location_based: false,
          },
          {
            type_of_order: "TD",
            key: "drop_params",
            label: "Delivery",
             is_location_based: false,
          },
        ],
      },
      T: {
        label: "Transfer",
        orderTypes: ["T"],
        is_location_based: true,
        types: [
          {
            type_of_order: "TR",
            key: "pickup_params",
            label: "Pickup",
            is_location_based: false,
          },
          {
            type_of_order: "TD",
            key: "drop_params",
            label: "Delivery",
             is_location_based: false,
          },
        ],
      },
      LH: {
        label: "Linehaul",
        orderTypes: ["LH"],
        is_location_based: true,
        types: [
          {
            type_of_order: "LH",
            key: "transfer_params",
            label: "Linehaul",
            is_location_based: true,
          },
        ],
      },
  
    },
    orderTypeLabels: {
      X: "Exchange",
      R: isOrgAirCargo ? "Pickup" : "Return",
      D: "Delivery",
      P: "Pickup",
      TD: "Delivery",
      TR: "Pickup",
      MD: "Delivery",
      MR: "Pickup",
      LH: "Linehaul",
      M: "Move",
      T: "Transfer",
      B: "Beyond",
      E: "Exclusive",
      H: "Hotshot",
    },
    enhancedOrderTypes: [
      {
        label: currentOrg?.code === "TA" ? "Pickup Only" : "Pickup",
        toolTipLabel: currentOrg?.code === "TA" ? "Pickup Only" : "Pickup Order",
        placement:"rightTop",
        message: [
          `A Pickup Order involves collecting items from a specified address and transporting them to ${I18n.t('general.warehouse_small')} for processing or storage.`,
          `Typically, these items are retrieved later by the shipper or another designated party at the ${I18n.t('general.warehouse_small')}. Delivery is not handled as part of a pickup order, as its focus is solely on transporting goods from the point of origin to the ${I18n.t('general.warehouse_small')}.`
        ],
        code: "R",
        type_of_order: 'R',
        description: `Items are picked up & brought to ${I18n.t('general.warehouse_small')}. Single-leg fulfillment.`,
        locations: [
          {
            type_of_loc: "PICKUP",
            label_code: 'R',
            loc_order_sequence: 0,
          },
        ],
      },
      {
        label: currentOrg?.code === "TA" ? "Delivery Only" : "Delivery",
        toolTipLabel: currentOrg?.code === "TA" ? "Delivery Only" : "Delivery Order",
        placement:"topRight",
        message: [
           `A Delivery Order involves transporting items already stored at ${I18n.t('general.warehouse_small')} to a specified location.`,
           `The focus of a delivery order is solely on completing the final leg of the journey from the ${I18n.t('general.warehouse_small')} to the destination. Pickup is not handled as part of a delivery order.`,
        ],
          code: "D",
        type_of_order: 'D',
        description: `Items at ${I18n.t('general.warehouse_small')} are delivered to a location. Single-leg fulfillment.`,
        locations: [
          {
            type_of_loc: "DELIVERY",
            label_code: 'D',
            loc_order_sequence: 0,
          },
        ],
      },
      {
        label: currentOrg?.code === "TA" ? "Pickup" : "2 Leg Pickup",
        toolTipLabel:"Pickup Order",
        placement:"rightTop",
        message: [
          `A Pickup Order involves collecting items from a specified address and transporting them to ${I18n.t('general.warehouse_small')} or consignee for processing or storage.`,
        ],
        code: "OR",
        type_of_order: 'T',
        description: `Pickup from a location to ${I18n.t('general.warehouse_small')} or location.`,
        locations: [
          {
            type_of_loc: "PICKUP",
            label_code: 'R',
            loc_order_sequence: 0,
          },
          {
            type_of_loc: "DELIVERY",
            label_code: 'D',
            loc_order_sequence: 1,
          }
        ],
      },
      {
        label: currentOrg?.code === "TA" ? "Delivery" : "2 Leg Delivery",
        toolTipLabel:"Delivery Order",
        placement:"topRight",
        message: [
           `A Delivery Order involves transporting items already stored at a ${I18n.t('general.warehouse_small')}  / from a location to a specified location.`,
        ],
        code: "OD",
        type_of_order: 'T',
        description: `Delivery from a ${I18n.t('general.warehouse_small')} or location to a location.`,
        locations: [
          {
            type_of_loc: "PICKUP",
            label_code: 'R',
            loc_order_sequence: 0,
          },
          {
            type_of_loc: "DELIVERY",
            label_code: 'D',
            loc_order_sequence: 1,
          },
        ],
      },
      {
        label: "Pickup",
        code: "SDR",
        type_of_order: 'R',
        description: "",
        locations: [
          {
            type_of_loc: "PICKUP",
            label_code: 'R',
            loc_order_sequence: 0,
          },
        ],
      },
      {
        label: "Pickup",
        code: "MDR",
        type_of_order: 'R',
        description: "",
        locations: [
          {
            type_of_loc: "PICKUP",
            label_code: 'R',
            loc_order_sequence: 0,
          },
        ],
      },
      {
        label: "Delivery",
        code: "SDD",
        type_of_order: 'D',
        description: "",
        locations: [
          {
            type_of_loc: "DELIVERY",
            label_code: 'D',
            loc_order_sequence: 0,
          },
        ],
      },
      {
        label: "Delivery",
        code: "MDD",
        type_of_order: 'D',
        description: "",
        locations: [
          {
            type_of_loc: "DELIVERY",
            label_code: 'D',
            loc_order_sequence: 0,
          },
        ],
      },
      {
        label: "Move Order",
        toolTipLabel:"Move Order",
        message:[
          "A Move Order is similar to a Pickup & Delivery Order but is managed as two separate (but still linked) orders—one for pickup and one for delivery. This approach allows each leg of the journey to be planned and executed independently while maintaining their connection to one another.",
          " Move Orders are ideal for scenarios where pickup and delivery need to be handled as distinct operations within your organization." 
        ],
        placement:"topRight",
        code: "M",
        type_of_order: 'M',
        description: "Pickup & Delivery but where each leg is managed as two separate orders.",
        locations: [
          {
            type_of_loc: "PICKUP",
            label_code: 'R',
            loc_order_sequence: 0,
          },
          {
            type_of_loc: "DELIVERY",
            label_code: 'D',
            loc_order_sequence: 1,
          },
        ],
      },
      {
        label: orgContextData?.currentOrg?.code == "MWE" ? "Local To Local" : "Pickup & Delivery",
        subtext: "(Originally Transfer)",
        toolTipLabel: orgContextData?.currentOrg?.code == "MWE" ? "Local To Local" : "Pickup & Delivery Order",
        message:[
          "A Pickup & Delivery Order is one of the most common delivery types, involving the collection of items from one address and transporting them to another. This process completes both the pickup and delivery legs in a two-leg journey.",
          `Intermediate storage is enabled by default if the ${I18n.t("general.appointment")} for the pickup and delivery are not on the same day, allowing for greater scheduling flexibility.`
        ],
        placement:"leftTop",
        code: "T",
        type_of_order: 'T',
        description: `Items are picked up & delivered with storage tied to ${I18n.t("general.appointment")} `,
        locations: [
          {
            type_of_loc: "PICKUP",
            label_code: 'R',
            loc_order_sequence: 0,
          },
          {
            type_of_loc: "DELIVERY",
            label_code: 'D',
            loc_order_sequence: 1,
          },
        ],
      },
      {
        label: "Beyond",
        toolTipLabel: "Beyond Order",
        message:[
          "A Beyond Order is used for deliveries outside the standard service area, involving the collection of items from one address and transporting them to a distant destination. This process completes both the pickup and delivery legs in a two-leg journey.",
          `Intermediate storage is enabled by default if the ${I18n.t("general.appointment")} for the pickup and delivery are not on the same day, allowing for greater scheduling flexibility.`
        ],
        placement:"leftTop",
        code: "B",
        type_of_order: 'T',
        description: `Items are picked up & delivered beyond standard service area with storage tied to ${I18n.t("general.appointment")}`,
        locations: [
          {
            type_of_loc: "PICKUP",
            label_code: 'R',
            loc_order_sequence: 0,
          },
          {
            type_of_loc: "DELIVERY",
            label_code: 'D',
            loc_order_sequence: 1,
          },
        ],
      },
      {
        label: "Exclusive",
        toolTipLabel: "Exclusive Order",
        message:[
          "An Exclusive Order is dedicated to high-priority shipments requiring a dedicated vehicle and direct transport, involving the collection of items from one address and transporting them directly to the destination. This process completes both the pickup and delivery legs in a two-leg journey.",
          `Intermediate storage is enabled by default if the ${I18n.t("general.appointment")} for the pickup and delivery are not on the same day, allowing for greater scheduling flexibility.`
        ],
        placement:"leftTop",
        code: "E",
        type_of_order: 'T',
        description: `Dedicated pickup & delivery service with storage tied to ${I18n.t("general.appointment")}`,
        locations: [
          {
            type_of_loc: "PICKUP",
            label_code: 'R',
            loc_order_sequence: 0,
          },
          {
            type_of_loc: "DELIVERY",
            label_code: 'D',
            loc_order_sequence: 1,
          },
        ],
      },
      {
        label: "Hotshot",
        toolTipLabel: "Hotshot Order",
        message:[
          "A Hotshot Order is an expedited delivery service for time-sensitive shipments, involving the immediate collection of items from one address and transporting them to the destination as quickly as possible. This process completes both the pickup and delivery legs in a two-leg journey.",
          `Intermediate storage is enabled by default if the ${I18n.t("general.appointment")} for the pickup and delivery are not on the same day, allowing for greater scheduling flexibility.`
        ],
        placement:"leftTop",
        code: "H",
        type_of_order: 'T',
        description: `Expedited pickup & delivery service with storage tied to ${I18n.t("general.appointment")}`,
        locations: [
          {
            type_of_loc: "PICKUP",
            label_code: 'R',
            loc_order_sequence: 0,
          },
          {
            type_of_loc: "DELIVERY",
            label_code: 'D',
            loc_order_sequence: 1,
          },
        ],
      },
      {
        label: "Recovery & Transfer (AFW)",
        toolTipLabel:"Recovery & Transfer Order",
        message:[
          "A Pickup & Delivery Order is one of the most common delivery types, involving the collection of items from one address and transporting them to another. This process completes both the pickup and delivery legs in a two-leg journey.",
           `Intermediate storage is enabled by default if the ${I18n.t("general.appointment")} for the pickup and delivery are not on the same day, allowing for greater scheduling flexibility.`
        ],
        placement:"leftTop",
        code: "AFW",
        type_of_order: 'T',
        description: "Items are picked up & delivered with & without storage to send AFW status.",
        locations: [
          {
            type_of_loc: "PICKUP",
            label_code: 'R',
            loc_order_sequence: 0,
          },
          {
            type_of_loc: "DELIVERY",
            label_code: 'D',
            loc_order_sequence: 1,
          },
        ],
      },
      {
        label: isCLIEnabled ? "Delivery Airline" : "Recovery & Delivery",
        toolTipLabel: isCLIEnabled ? "Delivery Airline" : "Recovery & Delivery Order",
        message:[
          `A ${isCLIEnabled ? "Delivery Airline" : "Recovery & Delivery"} Order involves collecting items—often from carriers or other sources—and transporting them to a ${I18n.t('general.warehouse_small')} for processing/sorting before delivering them to their final destination.`,
           "Unlike a Pickup & Delivery Order, this process is managed through the Recovery Board, which provides specialized tracking and coordination. Intermediate storage is enabled by default to allow for processing needs.",
           `${isCLIEnabled ? "Delivery Airline" : "Recovery & Delivery"} Orders are commonly used for routine pickups requiring handling before final delivery.`
        ],
        placement:"topRight",
        code: "RD",
        type_of_order: 'T',
        description: "Items are picked up, processed, & delivered. Tracked on Recovery Board.",
        locations: [
          {
            type_of_loc: "PICKUP",
            label_code: 'REC',
            loc_order_sequence: 0,
          },
          {
            type_of_loc: "DELIVERY",
            label_code: 'D',
            loc_order_sequence: 1,
          },
        ],
      },
      {
        label: "Recovery & Transfer",
        toolTipLabel:"Recovery & Transfer Order",
        message:[
          `A Recovery & Transfer Order involves collecting items—often from carriers or other sources—and transporting them to a ${I18n.t('general.warehouse_small')} for processing/sorting before handing them off to a third party for final delivery.`,
          "This two-leg process is managed through both the Recovery & Release Board, with intermediate storage enabled by default to allow for processing and coordination." ,
          "Recovery & Transfer Orders are commonly used when the final delivery is managed by a different service provider."
        ],
        placement:"leftTop",
        code: "RT",
        type_of_order: 'T',
        description: "Items are picked up & transferred to a 3rd party. Tracked on Recovery & Release Board.",
        locations: [
          {
            type_of_loc: "PICKUP",
            label_code: 'REC',
            loc_order_sequence: 0,
          },
          {
            type_of_loc: "DELIVERY",
            label_code: 'TRANS',
            loc_order_sequence: 1,
          },
        ],
      },
      {
        label: isCLIEnabled ? "P/U" : "Outbound Transfer",
        toolTipLabel: isCLIEnabled ? "P/U" : "Outbound Transfer Order",
        message:[
          `An ${isCLIEnabled ? "P/U" : "Outbound Transfer"} Order involves collecting items from a specified address and transporting them directly to a third party for final delivery.`,
          `Unlike Recovery & Transfer Orders, which are managed through the Recovery & Release Board, ${isCLIEnabled ? "P/U" : "Outbound Transfer"}s are tracked exclusively via the Release Board.`,
          `${isCLIEnabled ? "P/U" : "Outbound Transfer"} Orders are ideal when items are ready for immediate transfer to another delivery service.`],
        placement:"topRight",
        code: "OBT",
        type_of_order: 'T',
        description: "Items are picked up & transferred to a 3rd party. Tracked on Release Board.",
        locations: [
          {
            type_of_loc: "PICKUP",
            label_code: 'R',
            loc_order_sequence: 0,

          },
          {
            type_of_loc: "DELIVERY",
            label_code: 'TRANS',
            loc_order_sequence: 1,

          },
        ],
      },
      {
        label: "Cross Dock",
        toolTipLabel:"Cross Dock Order",
        message:[
          `A Cross Dock Order involves items being dropped off at a ${I18n.t('general.warehouse_small')} by a third party, processed/sorted, temporarily stored, and later picked up by another party for final delivery.`,
          " This process does not involve routing and focuses solely on providing temporary storage, processing, and dock ready transfer.",
          " Cross Dock Orders are ideal for scenarios where goods require short-term handling without additional transportation management."
        ],
        placement:"leftTop",
        code: "CDK",
        type_of_order: 'T',
        description: "Items are dropped, processed, & picked up by a 3rd party. No routing involved.",
        locations: [
          {
            type_of_loc: "DELIVERY",
            label_code: 'DRPF',
            loc_order_sequence: 0,
          },
          {
            type_of_loc: "PICKUP",
            label_code: 'R',
            loc_order_sequence: 1,
          },
        ],
      },
      {
        label: I18n.t('general.warehouse') + " Storage",
        toolTipLabel: I18n.t('general.warehouse') + " Storage Order",
        message:[
          `The ${I18n.t('general.warehouse')} Storage order type is for tracking and billing inventory that stays in the ${I18n.t('general.warehouse_small')} with no transportation involved. `,
          "Use it for long-term holds, overflow stock, customer-owned goods, or space utilization monitoring. Dispatch and routing options are permanently disabled to avoid accidental transport. ",
          `On creation, the system automatically applies the ${I18n.t('general.warehouse_small')} accessorial charge (based on duration, volume, or agreed rates) for accurate storage billing and provides visibility into ${I18n.t('general.warehouse_small')} capacity and inventory aging.`
        ],
        placement:"leftTop",
        code: "WO",
        type_of_order: 'T',
        description: `Tracks inventory storage. Cannot be dispatched like regular orders. Adds ${I18n.t('general.warehouse_small')} accessorial charge.`,
        locations: [
          {
            type_of_loc: "DELIVERY",
            label_code: 'DRPF',
            loc_order_sequence: 0,
          },
          {
            type_of_loc: "PICKUP",
            label_code: 'R',
            loc_order_sequence: 1,
          },
        ],
      },
      {
        label: "Pickup & Cross Dock",
        toolTipLabel:"Pickup & Cross Dock Order",
        message:[
          `A Pickup & Cross Dock Order involves collecting items from a specified address and transporting them to a ${I18n.t('general.warehouse_small')} for temporary storage and processing/sorting. The items are later retrieved by another party for final delivery.`,
          " The delivery portion of this order is excluded from routing, making this order type ideal for situations where goods require short-term storage and sorting before being handed off."
        ],
        placement:"leftTop",
        type_of_order: 'T',
        code: "PCDK",
        description: "Items are picked up, but delivered by 3rd party. No delivery routing.",
        locations: [
          {
            type_of_loc: "PICKUP",
            label_code: 'R',
            loc_order_sequence: 0,
          },
          {
            type_of_loc: "DELIVERY",
            label_code: 'CDK',
            loc_order_sequence: 1,
          },
        ],
      },
      {
        label: "Cross Dock & Delivery",
        toolTipLabel:"Cross Dock & Delivery Order",
        message:[
        `A Cross Dock & Delivery Order involves items being dropped off at a ${I18n.t('general.warehouse_small')} by a third party for temporary storage and processing/sorting before being delivered to their final destination.`,
         "Since Pickup is performed by another party, it is excluded from routing, allowing the focus to remain on the storage, sorting, and final delivery stages.",
         " This order type is ideal for 3rd party drop of goods requiring interim handling before delivery."
        ],
        placement:"rightTop",
        code: "CDKD",
        type_of_order: 'T',
        description:
          "Items dropped by 3rd party, processed, & delivered. No pickup routing.",
        locations: [
          {
            type_of_loc: "PICKUP",
            label_code: 'CDK',
            loc_order_sequence: 0,
          },
          {
            type_of_loc: "DELIVERY",
            label_code: 'D',
            loc_order_sequence: 1,
          },
        ],
      },
      {
        label: "Multi-Stop",
        toolTipLabel:"Multi-Stop Order",
        message:[
          "A Multi-Stop Order involves transporting items through a series of predefined stops and routes, often for dedicated trucks or drivers managing a specific circuit for a single account.",
          "This order type has its own built-in routing and thus cannot be routed with other orders.",
          " Multi-Stop Orders are commonly used for commonly scheduled routes or deliveries requiring multiple drop-offs for a dedicated account."
        ],
        placement:"rightTop",
        code: "MS",
        type_of_order: 'MS',
        description: "Orders have built-in stops & routing for dedicated trucks handling a route.",
        locations: [
          {
            type_of_loc: 'PICKUP',
            label_code: 'R',
            loc_order_sequence: 0,
          },
          {
            type_of_loc: 'DELIVERY',
            label_code: 'D',
            loc_order_sequence: 1,
          }],
      },
      {
        label: "Linehaul (Legacy)",
        toolTipLabel:"Linehaul (legacy)",
        message:[
          `A Linehaul involves transporting items or groups of orders between ${I18n.t('general.warehouses_small')} to optimize inventory or position goods for future deliveries.`,
          ` Managed through the Linehaul Board, this order type often includes sub-orders and detailed ${I18n.t('general.warehouse_small')} selection for seamless coordination.`,
          " Linehauls can also be created directly from the Order Management screen by selecting orders and choosing the Linehaul button in the option bar."
        ],
        placement:"leftTop",
        code: "LH",
        type_of_order: 'LH',
        description: `Orders moved between ${I18n.t('general.warehouses_small')} for inventory. Tracked on Linehaul Board.`,
        locations: [
          {
            type_of_loc: "PICKUP",
            label_code: 'ORN',
            loc_order_sequence: 0,
          },
          {
            type_of_loc: "DELIVERY",
            label_code: 'DST',
            loc_order_sequence: 1,
          },
        ],
      },
      {
        label: orgContextData?.currentOrg?.code == "MWE" ? "Terminal To Terminal" : "Linehaul",
        toolTipLabel: orgContextData?.currentOrg?.code == "MWE" ? "Terminal To Terminal" : "Linehaul",
        message:[
          `Transports orders solely between ${I18n.t('general.warehouses_small')} (often named after nearby airports, e.g., Airport to Airport), where they are held for customer or third-party pickup; dispatched and tracked on the Linehaul+ board, supporting multi-${I18n.t('general.warehouse_small')} routing on manifests without pickup or delivery segments.`
        ],
        placement:"leftTop",
        code: "NLH",
        type_of_order: 'LH',
        description: `Orders moved between ${I18n.t('general.warehouses_small')} only. Airport to Airport.`,
        locations: [
          {
            type_of_loc: "PICKUP",
            label_code: 'ORN',
            loc_order_sequence: 0,
          },
          {
            type_of_loc: "DELIVERY",
            label_code: 'DST',
            loc_order_sequence: 1,
          },
        ],
      },
      {
        label: orgContextData?.currentOrg?.code == "MWE" ? "Door To Terminal" : "Pickup & Linehaul",
        toolTipLabel: orgContextData?.currentOrg?.code == "MWE" ? "Door To Terminal" : "Pickup & Linehaul Order",
        placement:"rightTop",
        message: [
          `Handles pickup from origin (Door) and linehaul transport to a destination ${I18n.t('general.warehouse_small')} (often named after a nearby airport, e.g., Door to Airport), where orders are held for customer or third-party pickup; managed via the Linehaul+ board, with possible multi-${I18n.t('general.warehouse_small')} stops on manifests.`,
        ],
        code: "PLH",
        type_of_order: 'T',
        description: `Items are picked up and moved between ${I18n.t('general.warehouses_small')}. Door to Airport.`,
        locations: [
          {
            type_of_loc: "PICKUP",
            label_code: 'R',
            loc_order_sequence: 0,
          },
          {
            type_of_loc: "DELIVERY",
            label_code: 'DST',
            loc_order_sequence: 1,
          }
        ],
      },
      {
        label: orgContextData?.currentOrg?.code == "MWE" ? "Terminal To Door" : "Linehaul & Delivery",
        toolTipLabel: orgContextData?.currentOrg?.code == "MWE" ? "Terminal To Door" : "Linehaul & Delivery Order",
        placement:"topRight",
        message: [
          `Manages linehaul transport from a ${I18n.t('general.warehouse_small')} (often named after a nearby airport, e.g., Airport to Door) to the final ${I18n.t('general.warehouse_small')}, followed by delivery to the destination; linehauls are dispatched through the Linehaul+ board with possible multi-${I18n.t('general.warehouse_small')} stops on manifests.`,
        ],
        code: "LHD",
        type_of_order: 'T',
        description: `Orders moved between ${I18n.t('general.warehouses_small')} and then delivered. Airport to Door.`,
        locations: [
          {
            type_of_loc: "PICKUP",
            label_code: 'ORN',
            loc_order_sequence: 0,
          },
          {
            type_of_loc: "DELIVERY",
            label_code: 'D',
            loc_order_sequence: 1,
          },
        ],
      },
      {
        label: orgContextData?.currentOrg?.code == "SSF" ? "Line Haul" : orgContextData?.currentOrg?.code == "MWE" ? "Door To Door" : "Pickup,Linehaul & Delivery",
        toolTipLabel: orgContextData?.currentOrg?.code == "SSF" ? "Line Haul Order" : orgContextData?.currentOrg?.code == "MWE" ? "Door To Door" : "Pickup,Linehaul & Delivery Order",
        placement:"rightTop",
        message: [
          `Covers full door-to-door movement: pickup from origin, linehaul between ${I18n.t('general.warehouses_small')} (potentially multiple stops on manifests via Linehaul+ board), and final delivery to destination.`,
        ],
        code: "PLHD",
        type_of_order: 'T',
        description: `Items are picked up and moved between ${I18n.t('general.warehouses_small')} and then delivered. Door to Door.`,
        locations: [
          {
            type_of_loc: "PICKUP",
            label_code: 'R',
            loc_order_sequence: 0,
          },
          {
            type_of_loc: "DELIVERY",
            label_code: 'D',
            loc_order_sequence: 1,
          }
        ],
      },
      {
        label: "Airport to Airport",
        toolTipLabel: "Airport to Airport",
        placement:"rightTop",
        message: [
          "Handles pickup from origin, linehaul transport via air between airports, and delivery to final destination. Suitable for air freight shipments requiring door-to-door service with air transport between airport hubs.",
        ],
        code: "PLHD",
        extraCode: "APAD",
        type_of_order: 'T',
        description: "Items are picked up, transported via air between airports, and delivered to the final destination.",
        locations: [
          {
            type_of_loc: "PICKUP",
            label_code: 'R',
            loc_order_sequence: 0,
          },
          {
            type_of_loc: "DELIVERY",
            label_code: 'D',
            loc_order_sequence: 1,
          }
        ],
      },
      {
        label: "On-Site Service",
        toolTipLabel: "On-Site Service Order",
        placement: "rightTop",
        message: [
          "Scheduled on-site stop for services like setup or configuration, with no order delivery/pickup.",
        ],
        code: "OS",
        type_of_order: 'D',
        description: "Scheduled on-site stop for services like setup or configuration, with no order delivery/pickup.",
        locations: [
          {
            type_of_loc: "DELIVERY",
            label_code: 'D',
            loc_order_sequence: 0,
          },
        ],
      },
    ]
    
  }

  const getOrderStatus = (orderType) => {
    return ['TR', 'TD'].includes(orderType)
      ? orderConfig.orderTypes['M'].label
      : orderConfig.orderTypes[orderType]
      ? orderConfig.orderTypes[orderType].label
      : 'NA'
  }

  const getOrderKeys = (key = "id") => {
    return orderSelectedInfo ? orderSelectedInfo.map(o => o[key]) :[]
  }
  const getOrderTypeFromLabelCode = (orderType) => {
    const result = getValueFromArrayOfObjects(orderConfig.enhancedOrderTypes, "code", orderType, "type_of_order");
    if (result) return result;
    const match = orderConfig.enhancedOrderTypes.find(item => (item.code + (item?.extraCode || '')) === orderType);
    return match?.type_of_order;
  };
  const getLabelCodeOrdertypes = () => {
    const data = {};
    orderConfig.enhancedOrderTypes.forEach((orderTypeObj) => {
      data[orderTypeObj.code] = orderTypeObj.type_of_order;
    });
    return data;
  }

  const getLocationInfromLocType= (orderLabel, typeOfLoc, returnType = "obj") => {
    let orderTypeObj = _.find(orderConfig.enhancedOrderTypes, { code: orderLabel });
    if (!orderTypeObj) orderTypeObj = _.find(orderConfig.enhancedOrderTypes, { extraCode: orderLabel });
    const locObj = orderTypeObj ? _.find(orderTypeObj.locations, { type_of_loc: typeOfLoc }) : null
    if(returnType === "obj"){
      return locObj || null;
    } else {
      return locObj?.label_code || ""
    }
  };

  

  // P6c: fetch the org's active move-type catalog once and index it by
  // order_label for badge rendering. Gated on service_level_flow_enabled so
  // non-opted orgs pay no fetch and consumers keep the legacy icon. Auth-only
  // read (no RBAC gate); failures leave the index empty -> graceful fallback.
  useEffect(() => {
    if (orgSettings?.service_level_flow_enabled !== "true" || !currentOrg?.id) {
      setOmtByOrderLabel({});
      return undefined;
    }
    let cancelled = false;
    fetchMoveTypeCatalog({ orgId: currentOrg.id })
      .then((res) => {
        if (cancelled || !res?.success) return;
        const index = {};
        (res.data || []).forEach((omt) => {
          const label = omt?.move_type?.order_label;
          if (label) {
            index[label] = {
              short_code: omt.short_code,
              display_color: omt.display_color,
              alias_name: omt.alias_name,
              // Quote form: only quotable move types are offered there.
              is_quotable: omt?.move_type?.is_quotable,
              // Per-leg catalog data (leg_name, label_code, ...) so forms can
              // show the org's DB leg headings (parity with the order form).
              legs: omt?.move_type?.legs || [],
            };
          }
        });
        setOmtByOrderLabel(index);
      })
      .catch(() => {
        if (!cancelled) setOmtByOrderLabel({});
      });
    return () => {
      cancelled = true;
    };
  }, [currentOrg?.id, orgSettings?.service_level_flow_enabled]);

  const contextValues = {
    statusForActions,
    filterPlaceHolder,
    orderConfig,
    setFilterPlaceHolder,
    paginationData,
    setPaginationData,
    recordsPerPage,
    setRecordPerPage,
    selectedException,
    setSelectedException,
    getOrderStatus,
    getDefaultGlobalFilter,
    orderSelectedInfo, 
    setOrderSelectedInfo,
    getOrderKeys,
    statusForActions,
    getOrderTypeFromLabelCode,
    getLabelCodeOrdertypes,
    getLocationInfromLocType,
    omtByOrderLabel
  };

  return (
    <OrderConfigContext.Provider value={contextValues}>
      {children}
    </OrderConfigContext.Provider>
  );
};

export { OrderConfigContext, OrderConfigContextProvider };